import _ from 'lodash'
import type {Website} from '../types/website'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface TestBallotInfo {
  _id: string
  ballot_name: string
  ballot_slug: string
  customer_id: string
  url?: string
}

export interface TestBallotDictionaryEntry {
  _id: string
  ballot_name: string
  ballot_slug: string
  customer_id: string
}

export interface DesignReviewWebsite {
  url: string
  ballot_id: string | undefined
  customer_name: string
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const TEST_BALLOT_DICTIONARY: Record<string, TestBallotDictionaryEntry> = {
  PV: {
    _id: '647a4ef3f0acf81ff1b90708',
    ballot_name: 'Photo Voting (PV)',
    ballot_slug: 'pv',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  PVG: {
    _id: '6526a756803d6440cc7cd57e',
    ballot_name: 'Photo Voting with Groups (PVG)',
    ballot_slug: 'pvg',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  PVM: {
    _id: '66d0722f741f222380566c4a',
    ballot_name: 'Photo Voting Multiple Uploads (PVM)',
    ballot_slug: 'pvm',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  PS: {
    _id: '65316f621ca076625c4b54fc',
    ballot_name: 'Photo Submissions (PS)',
    ballot_slug: 'ps',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  PSG: {
    _id: '64e3aa7f755e646d842532d5',
    ballot_name: 'Photo Submissions with Groups (PSG)',
    ballot_slug: 'psg',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  PSM: {
    _id: '653aa519d685d709bc41cfbc',
    ballot_name: 'Photo Submissions Multiple Uploads (PSM)',
    ballot_slug: 'psm',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  PS09_SUBMISSIONS: {
    _id: '6a1e0622f9e38c075bd1aaed',
    ballot_name: 'Photo Submissions 09 (PS09)',
    ballot_slug: 'ps09-submissions',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  PSTV: {
    _id: '67d47e8f23ff8d040722c559',
    ballot_name: 'Photo Submissions TV? (PSTV)',
    ballot_slug: 'pstv',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  KHSG: {
    _id: '692de572955e9e08428d4af1',
    ballot_name: 'Kevin Henke Submissions with Groups',
    ballot_slug: 'khsg-01',
    customer_id: '6835dfd8b9ecd429b8af666f', // Kevin Henke (Developers)
  },
  KHV: {
    _id: '69496a1c76e6db084087e69b',
    ballot_name: 'Kevin Henke Voting',
    ballot_slug: 'khv-01',
    customer_id: '6835dfd8b9ecd429b8af666f', // Kevin Henke (Developers)
  },
  CAMPAIGN_JENNY_TAKE_2_ROUND_1: {
    _id: '69ab34b5ea3cc90761ac1708',
    ballot_name: 'Jenny Take 2 Round 1',
    ballot_slug: 'jenny-take-2-round-1',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  CAMPAIGN_JENNY_TAKE_2_ROUND_3: {
    _id: '69ab34b5ea3cc90761ac170a',
    ballot_name: 'Jenny Take 2 Round 3',
    ballot_slug: 'jenny-take-2-round-3',
    customer_id: '61af86ae1aa7da7c5082ad41',
  },
  PS07: {
    _id: '69eb8d8aa244e50760867366',
    ballot_name: 'Ballot PS07',
    ballot_slug: 'ps07',
    customer_id: '61af86ae1aa7da7c5082ad41', // scenethink.io
  },
  MANUAL_ENTRY: {
    _id: '69ca888110b1db076153b519',
    ballot_name: 'Ballot Manual Entry',
    ballot_slug: 'manual-entry',
    customer_id: '67e26ba24384d752f394bf86', // The Coast
  },
}

export const buildLocalBallotBySlug = (ballotSlug: string): TestBallotInfo | null => {
  let ballotInfo: TestBallotInfo | null = null
  if (_.keys(TEST_BALLOT_DICTIONARY).includes(ballotSlug.toUpperCase())) {
    ballotInfo = {
      ...TEST_BALLOT_DICTIONARY[ballotSlug.toUpperCase()],
      url: 'http://localhost:3100',
    }
  }
  return ballotInfo
}

export const buildWebsite = (testBallot: TestBallotInfo): Website => {
  const url = testBallot.url || 'http://localhost:3100/'

  // Build a proper Website object for localhost testing
  const website = {
    _id: `test-website-${testBallot._id}`,
    url: url,
    domain_type: 'localhost',
    is_universal_domain_owner: false,
    forward_options: {
      forward_type: 'ballot',
      url: url,
      customer_id: testBallot.customer_id,
      customer_name: testBallot.ballot_name,
      customer_slug: testBallot.ballot_slug,
    },
    date_time: new Date().toISOString(),
    list_of_universal_domain_subscribers: [],
    ballot_id: testBallot._id,
    __v: 0,
  }

  return website
}

export const LOCALHOST_TEST_BALLOT: TestBallotInfo | null = buildLocalBallotBySlug(
  process.env.NEXT_PUBLIC_LOCALHOST_TEST_BALLOT_SLUG || 'KHV',
)
export const LOCALHOST_TEST_WEBSITE: Website = buildWebsite(
  LOCALHOST_TEST_BALLOT as TestBallotInfo,
)

export const buildDesignReviewWebsites = (): DesignReviewWebsite[] => {
  const bestOfDevelopers = [
    'henkekm',
    'ali-zhao',
    'fullstacksr',
  ]
  const testingWebsites = _.map(bestOfDevelopers, (_developer, index) => {
    const count = index + 1
    const naughtedCount = count < 10 ? '0' + count : count
    return {
      url: `best-of-ui-git-design-review-${naughtedCount}-scenethink-team.vercel.app`,
      ballot_id: process.env.NEXT_PUBLIC_BETA_TESTING_BALLOT_ID,
      customer_name: 'Design Reviewer',
    }
  })
  return testingWebsites
}
